import {
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import { Atom } from '../state/atom.ts';
import { userFromAccount, type AuthenticatedUser } from './profile.ts';
import { devUser, isDevIdentity, loadAuthConfig } from './dev-identity.ts';

const DEFAULT_TENANT_ID = '9e37b9e9-05de-4906-b089-536f19689074';
const DEFAULT_CLIENT_ID = 'db7f3ed9-8868-4cea-a30a-015db776ec5e';
const DEFAULT_API_SCOPE = `api://${DEFAULT_CLIENT_ID}/roles`;

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID?.trim() || DEFAULT_TENANT_ID;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID?.trim() || DEFAULT_CLIENT_ID;
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE?.trim() || DEFAULT_API_SCOPE;
const browserOrigin = typeof window === 'undefined' ? '' : window.location.origin;

export type AuthStatus =
  | 'initializing'
  | 'unauthenticated'
  | 'signing-in'
  | 'authenticated'
  | 'error';

export interface AuthState {
  status: AuthStatus;
  user: AuthenticatedUser | null;
  error: string | null;
}

export const authState = new Atom<AuthState>({
  status: 'initializing',
  user: null,
  error: null,
});

const msal = new PublicClientApplication({
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: browserOrigin,
    postLogoutRedirectUri: browserOrigin,
  },
  cache: {
    // localStorage, not sessionStorage: sessionStorage is per-tab and is wiped
    // when the tab closes, so every fresh open of the app was a fresh sign-in.
    // localStorage keeps the account across tabs and browser restarts, and MSAL
    // renews the token silently from it.
    //
    // The exposure is unchanged in kind - both are readable by any script on
    // this origin, so an XSS on the SPA reaches the token either way. What
    // changes is how long a token sits there. Entra's own refresh-token
    // lifetime for a PKCE SPA still applies on top, so this makes sign-in
    // roughly daily rather than per-tab; it does not make it permanent.
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
});

const loginScopes = ['openid', 'profile', 'email', apiScope];
let initialization: Promise<void> | null = null;
let tokenRedirect: Promise<void> | null = null;
let forcedRenewal: Promise<string> | null = null;

function activate(account: AccountInfo): void {
  msal.setActiveAccount(account);
  authState.set({
    status: 'authenticated',
    user: userFromAccount(account),
    error: null,
  });
}

async function initializeOnce(): Promise<void> {
  authState.set({ status: 'initializing', user: null, error: null });
  try {
    // The backend decides whether there is a sign-in at all. Asking it is what
    // keeps the two halves from disagreeing - a SPA that insisted on Entra
    // against a backend that had it switched off would show a sign-in screen
    // that could never succeed.
    const config = await loadAuthConfig();
    if (!config.auth_enabled) {
      authState.set({ status: 'authenticated', user: devUser(), error: null });
      return;
    }
    await msal.initialize();
    const redirectResult = await msal.handleRedirectPromise({
      navigateToLoginRequestUrl: true,
    });
    const account = redirectResult?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0];
    if (account) activate(account);
    else authState.set({ status: 'unauthenticated', user: null, error: null });
  } catch (error) {
    authState.set({
      status: 'error',
      user: null,
      error: 'Microsoft sign-in could not be initialized.',
    });
    initialization = null;
    throw error;
  }
}

/** Initialize MSAL and restore an account after reload or an Entra redirect. */
export function initializeAuth(): Promise<void> {
  initialization ??= initializeOnce();
  return initialization;
}

export async function signIn(): Promise<void> {
  try {
    await initializeAuth();
    authState.set({ status: 'signing-in', user: null, error: null });
    await msal.loginRedirect({
      scopes: loginScopes,
      redirectStartPage: window.location.href,
    });
  } catch {
    authState.set({
      status: 'error',
      user: null,
      error: 'Microsoft sign-in failed.',
    });
  }
}

export async function signOut(): Promise<void> {
  await initializeAuth();
  // Nothing to sign out of - the menu offers a persona switcher instead.
  if (isDevIdentity()) return;
  const account = msal.getActiveAccount();
  markSessionUnauthorized();
  await msal.logoutRedirect({
    account,
    postLogoutRedirectUri: window.location.origin,
  });
}

/** Stop protected requests after the API rejects the current access token. */
export function markSessionUnauthorized(): void {
  // A 401 while running as a persona means the backend wants a real token, not
  // that this persona is finished; dropping to the sign-in screen would strand
  // a developer with no way back. Leave the state alone and let the error show.
  if (isDevIdentity()) return;
  msal.setActiveAccount(null);
  authState.set({ status: 'unauthenticated', user: null, error: null });
}

/** Return a current access token for the FastAPI resource. */
export async function getAccessToken(options: { forceRefresh?: boolean } = {}): Promise<string> {
  if (options.forceRefresh) {
    forcedRenewal ??= acquireAccessToken(true).finally(() => { forcedRenewal = null; });
    return forcedRenewal;
  }
  return acquireAccessToken(false);
}

async function acquireAccessToken(forceRefresh: boolean): Promise<string> {
  await initializeAuth();
  // With authentication off there is no token to get, and nothing that would
  // check one. The request still carries a bearer header so the shape of the
  // call is identical; `X-Dev-User` beside it is what names the persona.
  if (isDevIdentity()) return 'local-development';
  const account = msal.getActiveAccount();
  if (!account) {
    markSessionUnauthorized();
    throw new Error('Microsoft sign-in is required.');
  }

  try {
    const result = await msal.acquireTokenSilent({ scopes: [apiScope], account, forceRefresh });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Several API requests can discover an expired session together. Start
      // one redirect and stop showing a cached account as a usable session.
      tokenRedirect ??= (async () => {
        authState.set({ status: 'signing-in', user: null, error: null });
        try {
          await msal.acquireTokenRedirect({
            scopes: [apiScope], account, redirectStartPage: window.location.href,
          });
        } catch (redirectError) {
          tokenRedirect = null;
          markSessionUnauthorized();
          throw redirectError;
        }
      })();
      await tokenRedirect;
    }
    throw error;
  }
}
